import { useLayoutEffect, useRef, useState } from 'react';
import { spring, springs } from './engine';

// Section that opens and closes by animating its height (and fading its content).
// Stays mounted while closing. Toggling mid-way continues from where it is, with its speed.
// Starts without animation when it is open on the first render.
export function Collapse({ open, children, config = springs.collapse, className = '' }) {
  const [shown, setShown] = useState(open);
  const outer = useRef();
  const inner = useRef();
  const ctrl = useRef(null);
  const height = useRef(open ? null : 0); // null = natural height (open and at rest)
  const first = useRef(true);

  useLayoutEffect(() => () => ctrl.current?.stop(), []);

  useLayoutEffect(() => {
    if (first.current) { first.current = false; return; }
    if (open && !shown) { setShown(true); return; } // render the content first, animate next time
    const el = outer.current;
    const content = inner.current;
    if (!el || !content) return;

    const from = height.current ?? el.offsetHeight;
    const to = open ? content.offsetHeight : 0;
    const velocity = ctrl.current?.velocity ?? 0;
    ctrl.current?.stop();
    const render = (h) => {
      height.current = Math.max(0, h);
      el.style.height = `${height.current}px`;
      content.style.opacity = String(to || from ? Math.min(1, Math.max(0, height.current / Math.max(to, from, 1))) : 1);
    };
    ctrl.current = spring({
      from,
      to,
      velocity,
      config,
      precision: 0.5,
      onUpdate: render,
      onComplete: () => {
        if (open) {
          height.current = null;
          el.style.height = '';
          content.style.opacity = '';
        } else {
          setShown(false);
        }
      },
    });
  }, [open, shown]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!shown) return null;
  return (
    <div ref={outer} className={className} style={{ overflow: 'hidden', height: height.current === 0 ? 0 : undefined }}>
      <div ref={inner}>{children}</div>
    </div>
  );
}
